// ✅ src/pages/VehicleV2/SparesDetails.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/sparesDetails", sparesDetails);
//
// APIs used:
//   GET  http://nissi-india.com/api/sparesDetails/list
//   params:
//     loginUserId, branchId, branchType,
//     fromDate (dd/MM/yyyy), toDate (dd/MM/yyyy),
//     vehicleRegNo, vehicleCode, vehicleName, itemName,
//     page, limit
//
// ✅ Updated pattern like latest screens:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading states
// - safe mapping for list response
// - same UI structure style
// - supports date input as yyyy-MM-dd but sends dd/MM/yyyy to backend

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaFilter, FaUndo, FaArrowLeft } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/sparesDetails";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const formatDDMMYYYY = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const formatYMD = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const parseDDMMYYYY = (s) => {
  const str = String(s || "").trim();
  if (!str) return null;

  const parts = str.split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts.map((x) => Number(x));
  if (!dd || !mm || !yyyy) return null;

  const d = new Date(yyyy, mm - 1, dd);
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) {
    return null;
  }

  return d;
};

const parseAnyDateToYMD = (raw) => {
  const val = clean(raw);
  if (!val) return "";

  const d1 = parseDDMMYYYY(val);
  if (d1) return formatYMD(d1);

  const d2 = new Date(val);
  if (!Number.isNaN(d2.getTime())) return formatYMD(d2);

  return "";
};

const ymdToDDMMYYYY = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-");
  if (!y || !m || !d) return "";
  return `${pad2(d)}/${pad2(m)}/${y}`;
};

const isFuture = (d) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const x = new Date(d);
  x.setHours(0, 0, 0, 0);

  return x.getTime() > today.getTime();
};

const SparesDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const searchParams = useMemo(
    () => new URLSearchParams(location?.search || ""),
    [location]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [filters, setFilters] = useState({
    fromDateYMD: parseAnyDateToYMD(searchParams.get("fromDate") || ""),
    toDateYMD: parseAnyDateToYMD(searchParams.get("toDate") || ""),
    vehicleRegNo: toText(searchParams.get("vehicleRegNo") || ""),
    vehicleCode: toText(searchParams.get("vehicleCode") || ""),
    vehicleName: toText(searchParams.get("vehicleName") || ""),
    itemName: toText(searchParams.get("itemName") || ""),
  });

  const [rows, setRows] = useState([]);

  // paging
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  // ---- styles ----
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-white ${currentSkin.glass} text-black`
  }`;

  const mapRow = useCallback((r, idx = 0) => {
    return {
      id: toInt(r?.id ?? r?.fld_id ?? idx + 1),
      fld_date: toText(r?.fld_date ?? r?.date),
      fld_itemname: toText(r?.fld_itemname ?? r?.itemName),
      fld_uomname: toText(r?.fld_uomname ?? r?.uomName),
      fld_qty: toText(r?.fld_qty ?? r?.qty),
      fld_itemPrice: toText(r?.fld_itemPrice ?? r?.itemPrice),
      amount: toText(r?.amount ?? r?.total),
      _raw: r,
    };
  }, []);

  const validateDates = useCallback(() => {
    const f = filters.fromDateYMD ? new Date(filters.fromDateYMD) : null;
    const t = filters.toDateYMD ? new Date(filters.toDateYMD) : null;

    if (f && Number.isNaN(f.getTime())) {
      toast.warn("Invalid From Date");
      return false;
    }

    if (t && Number.isNaN(t.getTime())) {
      toast.warn("Invalid To Date");
      return false;
    }

    if (f && isFuture(f)) {
      toast.warn("Select from date lesser than current date");
      return false;
    }

    if (t && isFuture(t)) {
      toast.warn("Select to date lesser than current date");
      return false;
    }

    if (f && t && t.getTime() < f.getTime()) {
      toast.warn("Please select from date less than to date");
      return false;
    }

    return true;
  }, [filters.fromDateYMD, filters.toDateYMD]);

  /* ------------------------- LIST LOAD ------------------------- */
  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit, override = null, isFirst = false) => {
      try {
        if (isFirst) setPageLoading(true);
        else setLoading(true);

        const f = override || filters;

        const params = {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
          fromDate: ymdToDDMMYYYY(f.fromDateYMD),
          toDate: ymdToDDMMYYYY(f.toDateYMD),
          vehicleRegNo: clean(f.vehicleRegNo),
          vehicleCode: clean(f.vehicleCode),
          vehicleName: clean(f.vehicleName),
          itemName: clean(f.itemName),
          page: toInt(nextPage, 1),
          limit: toInt(nextLimit, 10),
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load spares details");
        }

        const list = Array.isArray(data?.list)
          ? data.list.map((r, idx) => mapRow(r, idx))
          : [];

        setRows(list);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "List failed"
        );
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    },
    [
      page,
      limit,
      filters,
      loginUserId,
      branchId,
      branchType,
      commonHeaders,
      mapRow,
    ]
  );

  /* ------------------------- ACTIONS ------------------------- */
  const handleSearchClick = useCallback(() => {
    if (!validateDates()) return;
    setPage(1);
    loadList(1, limit);
  }, [validateDates, loadList, limit]);

  const handleReset = useCallback(() => {
    const cleared = {
      fromDateYMD: "",
      toDateYMD: "",
      vehicleRegNo: "",
      vehicleCode: "",
      vehicleName: "",
      itemName: "",
    };

    setFilters(cleared);
    setPage(1);
    loadList(1, limit, cleared);
  }, [loadList, limit]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /* ------------------------- PAGINATION ------------------------- */
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  /* ------------------------- EFFECTS ------------------------- */
  useEffect(() => {
    loadList(1, limit, null, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page === 1 && limit === 10) return;
    loadList(page, limit);
  }, [page, limit, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SPARES DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={loading || pageLoading}
              >
                <FaArrowLeft />
                Back
              </button>

              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Spares Details
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
                disabled={loading || pageLoading}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                INFRA SCREEN : SPARES DETAILS
              </div>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    value={filters.fromDateYMD}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, fromDateYMD: e.target.value }))
                    }
                    className={inputClass}
                    disabled={loading || pageLoading}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    value={filters.toDateYMD}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, toDateYMD: e.target.value }))
                    }
                    className={inputClass}
                    disabled={loading || pageLoading}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    type="text"
                    value={filters.vehicleRegNo}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, vehicleRegNo: e.target.value }))
                    }
                    className={inputClass}
                    disabled={loading || pageLoading}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, vehicleCode: e.target.value }))
                    }
                    className={inputClass}
                    disabled={loading || pageLoading}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, vehicleName: e.target.value }))
                    }
                    className={inputClass}
                    disabled={loading || pageLoading}
                  />
                </div>

                <div>
                  <label className={labelClass}>Item Name</label>
                  <input
                    type="text"
                    value={filters.itemName}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, itemName: e.target.value }))
                    }
                    className={inputClass}
                    disabled={loading || pageLoading}
                  />
                </div>

                <div className="md:col-span-2 flex items-end gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() =>
                      setFilters((p) => ({
                        ...p,
                        fromDateYMD: formatYMD(new Date()),
                      }))
                    }
                    disabled={loading || pageLoading}
                    className={`px-4 py-2 rounded-lg border ${
                      isDark
                        ? "border-gray-700 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Set From = Today
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setFilters((p) => ({
                        ...p,
                        toDateYMD: formatYMD(new Date()),
                      }))
                    }
                    disabled={loading || pageLoading}
                    className={`px-4 py-2 rounded-lg border ${
                      isDark
                        ? "border-gray-700 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Set To = Today
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleReset}
                  type="button"
                  disabled={loading || pageLoading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow border transition-all duration-200 ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-700"
                      : "border-gray-300 text-gray-800 hover:bg-gray-100"
                  } ${loading || pageLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <FaUndo />
                  Clear / View All
                </button>

                <button
                  onClick={handleSearchClick}
                  disabled={loading || pageLoading}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading || pageLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  Search
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-[1100px] w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">Issue Date</th>
                    <th className="px-4 py-4 text-center font-semibold">Item Name</th>
                    <th className="px-4 py-4 text-center font-semibold">Uom Name</th>
                    <th className="px-4 py-4 text-center font-semibold">Quantity</th>
                    <th className="px-4 py-4 text-center font-semibold">Price</th>
                    <th className="px-4 py-4 text-center font-semibold">Total</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading || pageLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10">
                        No Records Found!
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => (
                      <tr
                        key={row.id || idx}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.fld_date}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.fld_itemname}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.fld_uomname}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.fld_qty}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.fld_itemPrice}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {row.amount}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-t ${
                isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
              }`}
            >
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {paginationText}
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  disabled={loading || pageLoading}
                  className={inputClass}
                  style={{ width: 120 }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>

                <button
                  disabled={!canPrev || loading || pageLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canPrev && !loading && !pageLoading
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                  }`}
                >
                  Prev
                </button>

                <div className={`text-sm px-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Page {page} / {totalPages}
                </div>

                <button
                  disabled={!canNext || loading || pageLoading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canNext && !loading && !pageLoading
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default SparesDetails;